import { useEffect, useMemo, useRef, useState } from 'react';
import { api, openConsoleStream, type AgentEntry, type AuthStatus, type ConsoleEvent, type MessageRow } from '../lib/api';
import { ConversationPane } from '../components/ConversationPane';
import { ContextValidatedStrip } from '../components/ContextValidatedStrip';
import { Composer } from '../components/Composer';
import { DaemonStatus } from '../components/DaemonStatus';
import { DaemonOfflineStrip } from '../components/DaemonOfflineStrip';
import { Prism } from '../components/Prism';
import { Wordmark } from '../components/Wordmark';
import { LiveIcon } from '../components/LiveIcon';
import { AgentDetailSheet } from '../components/AgentDetailSheet';
import { AgentSheet, AgentRail } from '../components/AgentPanel';
import { SessionsSheet } from '../components/SessionsSheet';
import { agentColorVars } from '../lib/agentColor';
import { prettyAgentTitle } from '../lib/agentName';

interface Props {
  auth: AuthStatus;
  onLogout: () => void | Promise<void>;
  onSignOutEverywhere: () => void | Promise<void>;
}

interface SessionState {
  id: string;
  agent_slug: string;
  messages: MessageRow[];
}

/**
 * Console page.
 *
 * Per docs/DESIGN-DECISIONS.md.
 *
 * Top bar (§1.2): 56 px, Prism + agent name on left (taps to open agent
 * bottom-sheet), DaemonStatus on right. Wordmark only on tablet+ (md:).
 * Phone hides sign-out in the bottom-sheet (C7); md:+ shows it on the bar.
 *
 * The Console wrapper applies agentColorVars() so every descendant can use
 * --agent-bg, --agent-bubble, --agent-edge, etc. without re-resolving.
 */
export function Console({ auth, onLogout, onSignOutEverywhere }: Props) {
  const [session, setSession] = useState<SessionState | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaultAgent, setDefaultAgent] = useState('jayson');
  // Model preference for FUTURE spawns. Empty string means "auto" (no
  // override; daemon uses claude's global default; V0 uses env.ANTHROPIC_MODEL).
  // Stored in localStorage so the choice survives reloads.
  const [defaultModel, setDefaultModelState] = useState<string>(() => {
    try {
      return typeof localStorage !== 'undefined' ? (localStorage.getItem('simpliloquy.model') ?? '') : '';
    } catch {
      return '';
    }
  });
  function setDefaultModel(value: string): void {
    setDefaultModelState(value);
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem('simpliloquy.model', value);
    } catch {
      // Quota / private mode: tolerate. State still updates.
    }
  }
  const [liveDaemon, setLiveDaemon] = useState<{ online: boolean; queued: number } | undefined>(undefined);
  const [agents, setAgents] = useState<AgentEntry[]>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [previewSlug, setPreviewSlug] = useState<string | null>(null);
  const [sendSeed, setSendSeed] = useState(0);
  const initRef = useRef(false);
  const streamRef = useRef<{ close: () => void } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Bumped on every session-switching action (selectAgent, pickSession,
  // init). Each async handler captures the current value and aborts its
  // setSession if the value has changed by the time the response lands.
  // Prevents a slow first request from clobbering a fast second request.
  const switchSeqRef = useRef(0);

  // Three sheets share the same surface region; opening any one must close
  // the other two. Wrapping the setters here keeps every call site honest
  // without manually remembering all three.
  function openPreview(slug: string | null) {
    setSheetOpen(false);
    setSessionsOpen(false);
    setPreviewSlug(slug);
  }

  /**
   * Initialize / re-initialize the Console: fetch the agent panel, list
   * sessions, and resume the most recent open one. If none, create a new
   * session against the default agent. Called on first mount and again
   * when another device closes our active session via spawn-new.
   */
  async function reinit() {
    const seq = ++switchSeqRef.current;
    try {
      const agentsResp = await api.listAgents();
      if (seq !== switchSeqRef.current) return;
      setDefaultAgent(agentsResp.default_agent);
      setAgents(agentsResp.agents);

      const list = await api.listSessions();
      if (seq !== switchSeqRef.current) return;
      const recent = list.sessions.find((s) => !s.closed_at) ?? null;
      if (recent) {
        const detail = await api.getSession(recent.id);
        if (seq !== switchSeqRef.current) return;
        setSession({
          id: detail.manifest.id,
          agent_slug: detail.manifest.agent_active,
          messages: detail.messages,
        });
        return;
      }
      const created = await api.createSession(agentsResp.default_agent);
      if (seq !== switchSeqRef.current) return;
      const detail = await api.getSession(created.session_id);
      if (seq !== switchSeqRef.current) return;
      setSession({
        id: detail.manifest.id,
        agent_slug: detail.manifest.agent_active,
        messages: detail.messages,
      });
    } catch (err) {
      if (seq !== switchSeqRef.current) return;
      setError(err instanceof Error ? err.message : 'failed to start session');
    }
  }

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    reinit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live console stream: subscribe when we have a session.
  useEffect(() => {
    if (!session) return;
    if (streamRef.current) {
      streamRef.current.close();
      streamRef.current = null;
    }
    streamRef.current = openConsoleStream(session.id, (ev: ConsoleEvent) => {
      if (ev.kind === 'daemon_status') {
        const body = ev.body as { online: boolean; queued: number };
        setLiveDaemon({ online: body.online, queued: body.queued });
        return;
      }
      if (ev.kind === 'session_closed') {
        // Another device closed this session (typically via spawn-new). Drop
        // the current view and re-init so we land on the new most-recent-open
        // session — the one the other device just created.
        setSession(null);
        void reinit();
        return;
      }
      if (
        ev.kind === 'dispatch_started' ||
        ev.kind === 'dispatch_output' ||
        ev.kind === 'dispatch_finished' ||
        ev.kind === 'interactive_prompt'
      ) {
        const body = ev.body as Record<string, unknown>;
        const dispatch_id = (body.dispatch_id as string | undefined) ?? null;
        // The DO tags broadcast envelopes with the canonical D1 row id (see
        // withRowId in worker/src/durable_object.ts). Use it as the React key
        // so refreshSession can replace the live row with the canonical row
        // without re-mounting the rendered component.
        const row_id = (body.row_id as string | undefined) ?? null;
        let content: string | null = null;
        if (ev.kind === 'dispatch_output') content = (body.chunk as string | undefined) ?? '';
        if (ev.kind === 'interactive_prompt') content = (body.prompt_text as string | undefined) ?? '';
        let metadata: string | null = null;
        try {
          metadata = JSON.stringify(body);
        } catch {
          metadata = null;
        }
        const row: MessageRow = {
          id: row_id ?? `live-${ev.kind}-${dispatch_id ?? 'x'}-${ev.ts}`,
          kind: ev.kind,
          agent_slug: null,
          dispatch_id,
          content,
          metadata,
          created_at: ev.ts,
        };
        setSession((prev) => {
          if (!prev) return prev;
          // Defensive dedupe: if a row with this id is already present (e.g.
          // refreshSession landed first), do not append a duplicate.
          if (prev.messages.some((m) => m.id === row.id)) return prev;
          return { ...prev, messages: [...prev.messages, row] };
        });
      }
    });
    return () => {
      streamRef.current?.close();
      streamRef.current = null;
    };
  }, [session?.id]);

  async function refreshSession() {
    if (!session) return;
    try {
      const detail = await api.getSession(session.id);
      // The live console stream pushes synthesized rows with `live-…` ids
      // for dispatch and interactive events. When the canonical D1 snapshot
      // lands, the live rows are now duplicates of the persisted ones (which
      // carry real D1 ids). Drop the live rows; keep only the canonical set.
      // (User messages also have local-* ids, but the new D1 row replaces
      //  the optimistic one, so we drop those too.)
      setSession({
        id: detail.manifest.id,
        agent_slug: detail.manifest.agent_active,
        messages: detail.messages,
      });
    } catch {
      // best-effort; the existing state stays
    }
  }

  async function send(text: string) {
    if (!session) return;
    setPending(true);
    setError(null);
    setSendSeed((s) => s + 1);
    const optimisticUserId = `local-${Date.now()}`;
    const userRow: MessageRow = {
      id: optimisticUserId,
      kind: 'user_message',
      agent_slug: session.agent_slug,
      dispatch_id: null,
      content: text,
      metadata: null,
      created_at: new Date().toISOString(),
    };
    setSession({ ...session, messages: [...session.messages, userRow] });

    // Per-turn AbortController. cancelTurn() aborts this; the worker
    // catches the resulting AbortError, persists a "turn cancelled"
    // agent_message row, and returns 499. We ignore the error path
    // beyond clearing the optimistic bubble.
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await api.postMessage(session.id, text, controller.signal);
      // Refetch the full session so authorize_proposal rows the agent emitted
      // during the tool-use loop appear in the conversation.
      await refreshSession();
    } catch (err) {
      const aborted =
        controller.signal.aborted ||
        (err instanceof Error && (err.name === 'AbortError' || /aborted|cancelled/i.test(err.message)));
      if (aborted) {
        // Pull the cancelled-turn row from D1 so the UI stays honest.
        await refreshSession();
      } else {
        const msg = err instanceof Error ? err.message : 'send failed';
        setError(msg);
        setSession((prev) =>
          prev ? { ...prev, messages: prev.messages.filter((m) => m.id !== optimisticUserId) } : prev,
        );
      }
    } finally {
      setPending(false);
      abortRef.current = null;
    }
  }

  /**
   * Cancel the in-flight turn. Aborts the fetch; the worker catches the
   * AbortError between Anthropic round trips, persists a "turn cancelled"
   * agent_message row, and returns 499. The composer becomes typeable
   * again on next render.
   */
  function cancelTurn() {
    abortRef.current?.abort();
  }

  const activeAgent = session?.agent_slug ?? defaultAgent;
  const agentVars = useMemo(() => agentColorVars(activeAgent), [activeAgent]);

  async function pickSession(session_id: string) {
    if (session_id === session?.id) return;
    const seq = ++switchSeqRef.current;
    try {
      const detail = await api.getSession(session_id);
      if (seq !== switchSeqRef.current) return; // a newer switch superseded
      setSession({
        id: detail.manifest.id,
        agent_slug: detail.manifest.agent_active,
        messages: detail.messages,
      });
      setError(null);
    } catch (err) {
      if (seq !== switchSeqRef.current) return;
      setError(err instanceof Error ? err.message : 'failed to switch session');
    }
  }

  /**
   * Spawn a fresh session against `slug`, closing the current one first.
   * Used by the + TopBar button and when this is called explicitly via
   * spawnNewSession.
   */
  async function spawnNewSession(slug: string) {
    const seq = ++switchSeqRef.current;
    try {
      const previousId = session?.id;
      if (previousId) {
        try {
          await api.closeSession(previousId);
        } catch {
          // Best-effort. The new session is still useful even if the close
          // request failed (network blip, expired cookie, etc).
        }
      }
      const created = await api.createSession(slug, defaultModel || null);
      if (seq !== switchSeqRef.current) return;
      const detail = await api.getSession(created.session_id);
      if (seq !== switchSeqRef.current) return;
      setSession({
        id: detail.manifest.id,
        agent_slug: detail.manifest.agent_active,
        messages: detail.messages,
      });
    } catch (err) {
      if (seq !== switchSeqRef.current) return;
      setError(err instanceof Error ? err.message : 'failed to switch agent');
    }
  }

  /**
   * Rail-tap handler. Tapping a *different* agent spawns a fresh session
   * with that agent. Tapping the *already-active* agent re-fetches the
   * current session — this preserves cross-device mirroring (a tap on
   * desktop won't blow away a session that phone is sharing) and gives
   * the user a manual "pull latest" if the live stream missed something.
   *
   * The "always spawn fresh" affordance is the + button in the TopBar.
   */
  async function selectAgent(slug: string) {
    if (slug === activeAgent) {
      if (session) await refreshSession();
      return;
    }
    await spawnNewSession(slug);
  }

  return (
    <div
      className="flex flex-col h-[100dvh] bg-night-900 text-paper-100"
      style={{
        ...agentVars,
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <TopBar
        agentSlug={activeAgent}
        liveDaemon={liveDaemon}
        onAgentTap={() => {
          setSessionsOpen(false);
          setPreviewSlug(null);
          setSheetOpen(true);
        }}
        onSessionsTap={() => {
          setSheetOpen(false);
          setPreviewSlug(null);
          setSessionsOpen(true);
        }}
        onSpawnNew={() => spawnNewSession(activeAgent)}
        onLogout={onLogout}
        device={auth.device}
        sessionId={session?.id}
        defaultModel={defaultModel}
        onModelChange={setDefaultModel}
      />

      <div className="flex-1 flex min-h-0">
        <AgentRail
          agents={agents}
          activeSlug={activeAgent}
          onSelect={selectAgent}
          onPreview={(slug) => openPreview(slug)}
          onLogout={onLogout}
          onSignOutEverywhere={onSignOutEverywhere}
        />
        <main className="flex-1 overflow-hidden flex flex-col min-h-0">
          <ContextValidatedStrip messages={session?.messages ?? []} />
          <ConversationPane
            messages={session?.messages ?? []}
            pending={pending}
            sessionId={session?.id}
            agentSlug={activeAgent}
            onAuthorizeDecided={refreshSession}
            onSpawn={() => send('boot')}
          />
          {error && (
            <div className="px-4 py-2 text-xs text-paper-300" role="alert">
              {error}
            </div>
          )}
          <DaemonOfflineStrip
            online={liveDaemon?.online ?? true}
            queued={liveDaemon?.queued ?? 0}
            seedKey={sendSeed}
          />
          <Composer
            onSubmit={send}
            disabled={!session || pending}
            agentSlug={activeAgent}
            onCancel={pending ? cancelTurn : undefined}
          />
        </main>
      </div>

      <AgentSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        agents={agents}
        activeSlug={activeAgent}
        onSelect={selectAgent}
        onPreview={(slug) => setPreviewSlug(slug)}
        onLogout={onLogout}
        onSignOutEverywhere={onSignOutEverywhere}
      />

      <SessionsSheet
        open={sessionsOpen}
        onClose={() => setSessionsOpen(false)}
        onPick={pickSession}
        activeId={session?.id}
      />

      <AgentDetailSheet
        open={previewSlug !== null}
        slug={previewSlug}
        onClose={() => setPreviewSlug(null)}
        onSpawn={async (slug) => {
          setPreviewSlug(null);
          await selectAgent(slug);
        }}
      />
    </div>
  );
}

interface TopBarProps {
  agentSlug: string;
  liveDaemon?: { online: boolean; queued: number };
  onAgentTap: () => void;
  onSessionsTap: () => void;
  onSpawnNew: () => void | Promise<void>;
  onLogout: () => void | Promise<void>;
  device: string;
  sessionId?: string;
  defaultModel: string;
  onModelChange: (m: string) => void;
}

/** Models exposed to the user. Value is the Anthropic model id (or empty
 *  for "auto"). The empty value means: omit the override, let each path
 *  pick its own default (claude global config for V1; env.ANTHROPIC_MODEL
 *  for V0). New sessions inherit the picker's current value. */
const MODEL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Auto' },
  { value: 'claude-opus-4-7', label: 'Opus' },
  { value: 'claude-sonnet-4-6', label: 'Sonnet' },
  { value: 'claude-haiku-4-5-20251001', label: 'Haiku' },
];

/**
 * Top bar per docs/DESIGN-DECISIONS.md §1.2 plus the V2 instrument-panel
 * chip strip on tablet+ (added 2026-05-06 after Mark's first review).
 *
 * Phone: Prism + LiveIcon + name + DaemonStatus.
 * Tablet+: Wordmark + Prism + LiveIcon + name, then chip strip (device,
 * session id), then DaemonStatus + sign-out.
 */
function TopBar({ agentSlug, liveDaemon, onAgentTap, onSessionsTap, onSpawnNew, onLogout, device, sessionId, defaultModel, onModelChange }: TopBarProps) {
  const sidShort = sessionId ? sessionId.slice(0, 8) : null;

  return (
    <header
      className="h-14 w-full bg-night-900 border-b border-night-600
                 flex items-center px-4 gap-3 flex-shrink-0"
    >
      {/* Wordmark, tablet+ only. */}
      <div className="hidden md:block mr-2">
        <Wordmark size="nav" />
      </div>

      {/* Active-agent trigger: opens bottom-sheet on phone, informational on rail breakpoints. */}
      <button
        type="button"
        onClick={onAgentTap}
        aria-label="Switch agent"
        className="flex items-center gap-2 min-w-0 max-w-[55%] md:max-w-none
                   h-full px-2 -mx-2 rounded-md
                   hover:bg-night-800 focus:outline-none focus:bg-night-800
                   transition-colors"
      >
        <Prism slug={agentSlug} size={20} active />
        <LiveIcon size={10} />
        <span className="font-semibold text-sm text-paper-100 truncate">
          {prettyAgentTitle(agentSlug)}
        </span>
      </button>

      {/* Chip strip: tablet+ instrument-panel register. */}
      <div className="hidden md:flex items-center gap-3 ml-3 pl-3 border-l border-night-700">
        <Chip label="device" value={device} />
        {sidShort && <Chip label="session" value={sidShort} />}
      </div>

      <div className="flex-1" />

      <select
        value={defaultModel}
        onChange={(e) => onModelChange(e.target.value)}
        aria-label="Default model for new sessions"
        title="Model used when you spawn a new session"
        className="bg-night-800 border border-night-600 text-paper-100 rounded-md
                   px-2 py-1 text-sm h-9
                   hover:border-paper-300 focus:outline-none focus:border-paper-300
                   transition-colors"
      >
        {MODEL_OPTIONS.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>

      <button
        type="button"
        onClick={() => onSpawnNew()}
        aria-label="New session"
        title="New session"
        className="w-11 h-11 flex items-center justify-center rounded-md
                   hover:bg-night-800 focus:outline-none focus:bg-night-800
                   transition-colors text-paper-300 hover:text-paper-100"
      >
        <PlusIcon />
      </button>

      <button
        type="button"
        onClick={onSessionsTap}
        aria-label="Past sessions"
        title="Past sessions"
        className="w-11 h-11 flex items-center justify-center rounded-md
                   hover:bg-night-800 focus:outline-none focus:bg-night-800
                   transition-colors text-paper-300 hover:text-paper-100"
      >
        <HistoryIcon />
      </button>

      <DaemonStatus live={liveDaemon} />

      <button
        type="button"
        onClick={onLogout}
        className="hidden md:inline-block text-xs text-paper-400 hover:text-paper-100 transition-colors ml-2"
      >
        Sign out
      </button>
    </header>
  );
}

function PlusIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M8 3 L8 13 M3 8 L13 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3 3 L3 7 L7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.4 7 A5 5 0 1 0 5 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 5 L8 8.5 L10.5 9.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Chip({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col leading-none">
      <span
        className="font-mono text-[10px] uppercase tracking-mono-wide"
        style={{ color: 'var(--agent-fg-soft)' }}
      >
        {label}
      </span>
      <span className="font-mono text-[11px] text-paper-200 mt-0.5">{value}</span>
    </div>
  );
}

